import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { batchByproductsQuery } from '@/queries/byproduct.query';
import { layersQuery } from '@/queries/layer.query';
import { landxmlExportTrackingAtom } from '@/atoms/landxmlExport-progress.atom';

/**
 * Mounted once per project shell (next to `useVectorExportProgress`).
 *
 * Unlike VECTOR_EXPORT, a LANDXML_EXPORT job does NOT finish the moment its
 * own (leaf) job reaches PROCESSED: postprocess creates a *separate* POST
 * job, scheduled after the leaf, that actually writes the derived
 * MODEL/LANDXML layer (see `server/background/src/functions/postprocess.ts`,
 * the `leafJob.type === 'LANDXML_EXPORT'` branch, which stamps
 * `metadata.batchId`/`metadata.derivedFrom`/`metadata.sourceProcessJobId` on
 * the new layer). So — mirroring `useDeviationProgress` — success is
 * detected by polling `['layers', projectId]` for a layer whose
 * `metadata.batchId` matches the tracker's batch, not by the leaf job's own
 * status.
 *
 * Failure, however, can come from EITHER job in the batch — the
 * LANDXML_EXPORT leaf (e.g. bad geometry) or the POST job that writes the
 * layer (e.g. a DB error after the leaf already reported PROCESSED) — so we
 * match a FAILED status against any job sharing the tracker's `batchId`,
 * not just the leaf. Missing this meant a POST-job failure left the leaf
 * looking PROCESSED forever with no layer ever appearing, leaking the
 * tracker/toast indefinitely.
 *
 * A GROUPED export puts several LANDXML_EXPORT leaves in one batch (one per
 * group → one layer each), and every resulting layer carries that same
 * `metadata.batchId`. So success means `tracker.expectedLayers` matching
 * layers, not the first one — otherwise the toast reports "finished" while
 * the remaining courses are still tessellating. Until then the toast counts
 * up ("3 of 9 surfaces exported").
 */
export function useLandxmlExportProgress(projectId: string) {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const [trackers, setTrackers] = useAtom(landxmlExportTrackingAtom);

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    // `cancelRefetch: false`: `refetch()` cancels the request still in
    // flight by default, so a response slower than this 2 s interval is
    // never delivered and the toast spins for ever. A tick JOINS the poll
    // already running (see `useDeviationProgress` for the reported case).
    const tick = () => {
      void refetchJobs({ cancelRefetch: false });
      void refetchLayers({ cancelRefetch: false });
    };
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];
    const layers = layersData?.layers ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        const jobItem = jobs.find((j) => j.batchId === tracker.batchId && j.type === 'LANDXML_EXPORT');
        // Any job in the batch failing (leaf OR the POST job that writes the
        // layer) is a failure for this tracker — see doc comment above.
        const failedJob = jobs.find((j) => j.batchId === tracker.batchId && j.status === 'FAILED');

        if (failedJob) {
          const detail =
            typeof failedJob.errorLogs === 'string' && failedJob.errorLogs.trim().length > 0
              ? failedJob.errorLogs.trim()
              : null;
          toast.error(detail ?? t('landxmlExport.failedToast', { name: tracker.sourceLayerName }), {
            id: tracker.toastId,
            duration: 8000,
          });
          next.splice(i, 1);
          changed = true;
          continue;
        }

        // Success path: wait for the derived layer(s) themselves (see doc
        // comment above) rather than the leaf job's status — the leaf can
        // already be PROCESSED while the POST job that writes the layer is
        // still queued.
        const newLayers = layers.filter((l) => {
          const meta = (l.metadata ?? {}) as { batchId?: string };
          return meta.batchId === tracker.batchId;
        });
        const expected = Math.max(1, tracker.expectedLayers ?? 1);
        if (newLayers.length > 0 && newLayers.length < expected) {
          // Partial: keep the tracker open, but show the progress.
          toast.loading(
            t('landxmlExport.runningToastProgress', {
              name: tracker.sourceLayerName,
              done: newLayers.length,
              total: expected,
            }),
            { id: tracker.toastId, duration: Infinity },
          );
          // The layers that DID land should show up in the layer panel now.
          void queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
          continue;
        }
        const newLayer = newLayers[0];
        if (newLayer) {
          const meta = (newLayer.metadata ?? {}) as { sourceProcessJobId?: string };
          const completedJobId = meta.sourceProcessJobId ?? jobItem?.id;
          // The layer just appeared in this poll's snapshot — invalidate so
          // every other observer of ['layers', projectId] (the layer panel)
          // refetches and shows it right away.
          void queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
          toast.success(
            newLayers.length > 1
              ? t('landxmlExport.completedToastMany', {
                  name: tracker.sourceLayerName,
                  count: newLayers.length,
                })
              : t('landxmlExport.completedToast', { name: tracker.sourceLayerName }),
            {
              id: tracker.toastId,
              duration: 10000,
              action: {
                label: t('landxmlExport.download'),
                onClick: async () => {
                  try {
                    const data = await execute(batchByproductsQuery, { id: tracker.batchId });
                    const bps = data.batch?.byproducts ?? [];
                    // A grouped export produced one file per group: hand over
                    // all of them rather than an arbitrary first.
                    const picked = bps.length > 1 ? bps : bps.filter((b) => b.processJobId === completedJobId);
                    const files = picked.length > 0 ? picked : bps;
                    if (files.length === 0) {
                      toast.error(t('landxmlExport.byproductNotFound'));
                      return;
                    }
                    for (const bp of files) window.open(bp.downloadUrl, '_blank');
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : String(err));
                  }
                },
              },
            },
          );
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, layersData, hasTrackers, projectId, setTrackers, t, queryClient]);
}
